import React from "react";

import { useAppSelector } from "../../../app/hooks";
import { FleetOptimizationInput } from "../../../models";
import { DumpsterSize, dumpsterSizeToText } from "../../../models/Order";
import {
  stopLabel,
  BALANCE_STOPS,
  COMPACTNESS_STOPS,
  TIME_WINDOW_STOPS,
  END_TIME_STOPS,
  OVERTIME_STOPS,
} from "../NewRunConfig/PenaltySlider";

import "./InputSummary.scss";

// A collapsible summary of the constraints a run was given: assignments, the
// optimizer settings, and any per-job overrides. Names resolve from redux
// (drivers/vehicles are global; deliveries/pickups are loaded for the run date).
export function InputSummary({ input }: { input: FleetOptimizationInput }) {
  const [open, setOpen] = React.useState(false);
  const drivers = useAppSelector((s) => s.driversV2.drivers) || [];
  const vehicles = useAppSelector((s) => s.vehicles.vehicles) || [];
  const allDeliveries = useAppSelector((s) => s.deliveries.deliveries);
  const allPickups = useAppSelector((s) => s.pickups.pickups);
  // Forced dump-yard ids are dump-yard TEMPLATE ids (see NewRunConfig), so resolve
  // the name from templates, not the date-scoped /dump_yards instances.
  const dumpYards = useAppSelector((s) => s.dumpYards.templates) || [];

  const driverName = (id: string) => {
    const d = drivers.find((x: any) => x.userId === id || x.id === id);
    return d?.name ?? `Driver #${id}`;
  };
  const vehicleName = (id: string) =>
    vehicles.find((v: any) => v.id === id)?.label ?? `Vehicle #${id}`;
  const jobName = (id: string) => {
    const d = allDeliveries.find((x) => x.id === id);
    if (d) return d.name;
    const p = allPickups.find((x) => x.id === id);
    if (p) return p.name;
    return `#${id}`;
  };
  const yardName = (id: string) => {
    const y = dumpYards.find((x: any) => x.id === id);
    return y?.name || y?.address || `#${id}`;
  };

  // Soft-penalty knobs are 5-position sliders on the New Run page — render the same
  // intensity labels (Moderate, Strict, …) here instead of the raw numbers, snapping
  // to the nearest position just like the slider does. Maintenance stays a raw $/mi
  // value (it's a plain numeric input, not a slider).
  const settings: string[] = [];
  if (input.dumpFeeRankWeight != null)
    settings.push(`Dump-fee weight: ${Math.round(input.dumpFeeRankWeight * 100)}%`);
  if (input.compactnessPenalty != null)
    settings.push(`Compactness: ${stopLabel(input.compactnessPenalty, COMPACTNESS_STOPS)}`);
  if (input.maintenancePerMile != null)
    settings.push(`Maintenance $${input.maintenancePerMile}/mi`);
  if (input.overtimeAllowed) settings.push("Overtime allowed");
  else if (input.overtimePenalty != null)
    settings.push(`Overtime: ${stopLabel(input.overtimePenalty, OVERTIME_STOPS)}`);
  if (input.endTimePenalty != null)
    settings.push(`End-time: ${stopLabel(input.endTimePenalty, END_TIME_STOPS)}`);
  if (input.timeWindowPenalty != null)
    settings.push(`Time-window: ${stopLabel(input.timeWindowPenalty, TIME_WINDOW_STOPS)}`);
  if (input.balancePenalty != null)
    settings.push(`Balance: ${stopLabel(input.balancePenalty, BALANCE_STOPS)}`);
  if (input.allowCarryover)
    settings.push(
      input.carryoverThreshold != null
        ? `Carryover (≥${input.carryoverThreshold} mi)`
        : "Carryover"
    );
  if (input.allowCarryover && input.maxCarryovers != null && input.maxCarryovers > 0)
    settings.push(`Max carryovers ${input.maxCarryovers}`);

  const windows = input.timeWindows ?? [];
  const locks = input.locks ?? [];
  const exclude = input.exclude ?? [];
  const topoff = input.allowTopoffForJobs ?? [];
  const wastes = input.pickupWasteTypes ?? [];
  const dumpWeights = input.pickupDumpWeights ?? [];
  const dumpYardOverrides = input.pickupDumpYards ?? [];
  const precedences = input.precedences ?? [];
  const swaps = input.swaps ?? [];
  const overrideCount =
    locks.length +
    exclude.length +
    topoff.length +
    wastes.length +
    dumpWeights.length +
    dumpYardOverrides.length +
    precedences.length +
    swaps.length;

  return (
    <div className="input-summary">
      <button
        className="input-summary-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {open ? "▾" : "▸"} Run inputs — {input.driverAssignments.length} drivers
        {settings.length > 0 && `, ${settings.length} setting(s)`}
        {windows.length > 0 && `, ${windows.length} window(s)`}
        {overrideCount > 0 && `, ${overrideCount} job override(s)`}
      </button>

      {open && (
        <div className="input-summary-body">
          {input.date && (
            <div className="is-group">
              <div className="is-label">Date</div>
              <div className="is-chips">
                <span className="is-chip">{input.date}</span>
              </div>
            </div>
          )}

          <div className="is-group">
            <div className="is-label">Assignments</div>
            <ul>
              {input.driverAssignments.map((a, i) => (
                <li key={i}>
                  {driverName(a.driverId)} · {vehicleName(a.vehicleId)}
                  {a.startingDumpster &&
                    ` · holds ${
                      dumpsterSizeToText[a.startingDumpster.size as DumpsterSize] ??
                      a.startingDumpster.size
                    } (${a.startingDumpster.state})`}
                  {a.startTime && ` · from ${a.startTime}`}
                  {a.endTime && ` · ends ${a.endTime}`}
                </li>
              ))}
            </ul>
          </div>

          {settings.length > 0 && (
            <div className="is-group">
              <div className="is-label">Optimizer settings</div>
              <div className="is-chips">
                {settings.map((s, i) => (
                  <span key={i} className="is-chip">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {windows.length > 0 && (
            <div className="is-group">
              <div className="is-label">Time windows</div>
              <ul>
                {windows.map((w, i) => (
                  <li key={`w${i}`}>
                    {jobName(w.jobId)} · {w.start}–{w.end}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {overrideCount > 0 && (
            <div className="is-group">
              <div className="is-label">Per-job overrides</div>
              <ul>
                {locks.map((l, i) => (
                  <li key={`l${i}`}>
                    Pin {jobName(l.jobId)} → {driverName(l.driverId)}
                  </li>
                ))}
                {exclude.map((id, i) => (
                  <li key={`e${i}`}>Exclude {jobName(id)}</li>
                ))}
                {topoff.map((id, i) => (
                  <li key={`t${i}`}>Top-off {jobName(id)}</li>
                ))}
                {wastes.map((w, i) => (
                  <li key={`w${i}`}>
                    Waste {jobName(w.pickupId)} → {w.wasteType}
                  </li>
                ))}
                {dumpWeights.map((w, i) => (
                  <li key={`dw${i}`}>
                    Dump weight {jobName(w.pickupId)} → {w.weight} t
                  </li>
                ))}
                {dumpYardOverrides.map((y, i) => (
                  <li key={`dy${i}`}>
                    Dump yard {jobName(y.pickupId)} → {yardName(y.dumpYardId)}
                  </li>
                ))}
                {precedences.map((p, i) => (
                  <li key={`p${i}`}>
                    Must follow {jobName(p.afterJobId)} → after{" "}
                    {jobName(p.beforeJobId)}
                  </li>
                ))}
                {swaps.map((s, i) => (
                  <li key={`s${i}`}>
                    Same-box swap {jobName(s.deliveryJobId)} → returns pickup{" "}
                    {jobName(s.pickupJobId)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
